import pantoneData from "../constant/pantonne.constant.json";
import {
  convertRgbToLab,
  differenceEuclidean,
  differenceCiede2000,
  parse,
} from "culori";

export interface PantoneColor {
  name: string;
  uniqueCode: string;
  hex: string;
  lab: {
    l: number;
    a: number;
    b: number;
  };
}

interface DistanceSortableColor {
  hex: string;
  name?: string;
  label?: string;
}

export interface ColorDistanceMatch<T> {
  color: T;
  distance: number;
  sortScore: number;
}

// Weights for the hybrid color distance score used in sortColorsByDistance.
// Three perceptual distance metrics are computed, normalized, then blended:
//   - sRGB linear (0.5): fast Euclidean distance in raw RGB space — biases toward
//     colors that look similar on screen in a naive "pixel" sense
//   - CIE76 (0.2): Euclidean distance in L*a*b* space — better perceptual accuracy
//     than sRGB but still has known weaknesses for saturated colors
//   - CIEDE2000 (0.3): industry-standard perceptual distance — most accurate but
//     given less than sRGB because pure perceptual accuracy can feel "off" to users
//     when the linear component is ignored entirely
// The sum of weights must equal 1.0.
const HYBRID_LINEAR_WEIGHT = 0.5;
const HYBRID_CIE76_WEIGHT = 0.2;
const HYBRID_DE2000_WEIGHT = 0.3;

let pantoneColorsCache: PantoneColor[] | null = null;

export const getPantoneColors = (): PantoneColor[] => {
  if (pantoneColorsCache) {
    return pantoneColorsCache;
  }

  try {
    pantoneColorsCache = pantoneData as PantoneColor[];
    return pantoneColorsCache;
  } catch (error) {
    return [];
  }
};

export const normalizePantoneQuery = (input: string): string => {
  return input.toLowerCase().replace(/\s+/g, " ").trim();
};

interface ParsedPantone {
  number: string;
  suffix: string;
}

const parsePantoneForMatching = (input: string): ParsedPantone | null => {
  const normalized = input
    .toLowerCase()
    .replace(/^(pantone|pms)\s*/i, "")
    .trim();

  const match = normalized.match(/^(\d+)\s*([a-z])?$/i);

  if (match) {
    return {
      number: match[1],
      suffix: match[2] || "",
    };
  }

  return null;
};

export const matchesPantone = (colorName: string, query: string): boolean => {
  const normalizedName = normalizePantoneQuery(colorName);
  const normalizedQuery = normalizePantoneQuery(query);

  if (normalizedName.includes(normalizedQuery)) {
    return true;
  }

  const parsedName = parsePantoneForMatching(colorName);
  const parsedQuery = parsePantoneForMatching(query);

  if (!parsedName || !parsedQuery) {
    return false;
  }

  if (parsedName.number !== parsedQuery.number) {
    return false;
  }

  if (!parsedQuery.suffix) {
    return true;
  }

  return parsedName.suffix.toLowerCase() === parsedQuery.suffix.toLowerCase();
};

export const searchPantoneColors = (
  query: string,
  limit: number = 10
): PantoneColor[] => {
  const pantoneColors = getPantoneColors();
  const matches: PantoneColor[] = [];

  const normalizedQuery = query.trim().toLowerCase();
  const isPmsOnlyQuery = normalizedQuery === "pms";
  const isPantoneOnlyQuery = normalizedQuery === "pantone";

  if (isPmsOnlyQuery || isPantoneOnlyQuery) {
    const limitedColors = pantoneColors.slice(0, limit);
    return limitedColors;
  }

  for (const color of pantoneColors) {
    if (matchesPantone(color.name, query)) {
      matches.push(color);
      if (matches.length >= limit) break;
    }
  }

  return matches;
};

export const hexToRgb = (
  hex: string
): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

export const normalizeHexColor = (hex: string): string => {
  let normalized = hex.trim().toUpperCase();

  if (!normalized.startsWith("#")) {
    normalized = `#${normalized}`;
  }

  if (normalized.length === 4) {
    normalized =
      "#" +
      normalized[1] +
      normalized[1] +
      normalized[2] +
      normalized[2] +
      normalized[3] +
      normalized[3];
  }

  return normalized;
};

export const isValidHexColor = (hex: string): boolean => {
  return /^#?([A-Fa-f0-9]{6})$/.test(hex.trim());
};

export const getColorDistance = (hex1: string, hex2: string): number => {
  if (!isValidHexColor(hex1) || !isValidHexColor(hex2)) {
    return Infinity;
  }

  try {
    const distance = differenceCiede2000()(
      normalizeHexColor(hex1),
      normalizeHexColor(hex2)
    );

    return Number.isFinite(distance) ? distance : Infinity;
  } catch {
    return Infinity;
  }
};

const getSrgbDistance = (hex1: string, hex2: string): number => {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);

  if (!rgb1 || !rgb2) {
    return Infinity;
  }

  const dr = rgb1.r - rgb2.r;
  const dg = rgb1.g - rgb2.g;
  const db = rgb1.b - rgb2.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
};

const getCie76Distance = differenceEuclidean("lab");

const getHexCie76Distance = (hex1: string, hex2: string): number => {
  const rgb1 = parse(hex1);
  const rgb2 = parse(hex2);

  if (!rgb1 || !rgb2) {
    return Infinity;
  }

  const lab1 = convertRgbToLab(rgb1 as Parameters<typeof convertRgbToLab>[0]);
  const lab2 = convertRgbToLab(rgb2 as Parameters<typeof convertRgbToLab>[0]);

  if (!lab1 || !lab2) {
    return Infinity;
  }

  return getCie76Distance(lab1, lab2);
};

const normalizeDistances = (values: number[]): number[] => {
  if (!values.length) {
    return [];
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = max - min;

  if (spread <= 1e-9) {
    return values.map(() => 0);
  }

  return values.map((value) => (value - min) / spread);
};

const getColorSortKey = (color: DistanceSortableColor): string =>
  color.label || color.name || color.hex;

// Ranks colors by similarity to a query hex using a hybrid of three distance
// metrics (sRGB, CIE76, CIEDE2000). Each metric is normalized across candidates
// then blended via weighted sum — see HYBRID_*_WEIGHT constants for the rationale.
export const sortColorsByDistance = <T extends DistanceSortableColor>(
  queryHex: string,
  colors: T[],
  maxDistance: number
): ColorDistanceMatch<T>[] => {
  const normalizedQuery = normalizeHexColor(queryHex);
  const candidates = colors
    .map((color) => {
      const normalizedHex = normalizeHexColor(color.hex);
      return {
        color,
        distance: getColorDistance(normalizedQuery, normalizedHex),
        srgbDistance: getSrgbDistance(normalizedQuery, normalizedHex),
        cie76Distance: getHexCie76Distance(normalizedQuery, normalizedHex),
      };
    })
    .filter((item) => item.distance <= maxDistance);

  // Each metric operates on a different scale (sRGB: 0–441, CIE76/DE2000: ~0–100),
  // so raw values can't be combined directly. Min-max normalization maps each
  // metric to [0, 1] relative to the current candidate set before blending.
  const linearNorms = normalizeDistances(
    candidates.map((item) => item.srgbDistance)
  );
  const cie76Norms = normalizeDistances(
    candidates.map((item) => item.cie76Distance)
  );
  const de2000Norms = normalizeDistances(
    candidates.map((item) => item.distance)
  );

  return candidates
    .map((item, index) => ({
      color: item.color,
      distance: item.distance,
      // Weighted blend of the three normalized scores. Lower = closer match.
      // Weights are tuned so sRGB keeps results feeling visually intuitive while
      // DE2000 corrects for perceptual non-linearities (e.g. hue, chroma shifts).
      sortScore:
        HYBRID_LINEAR_WEIGHT * linearNorms[index] +
        HYBRID_CIE76_WEIGHT * cie76Norms[index] +
        HYBRID_DE2000_WEIGHT * de2000Norms[index],
    }))
    .sort((a, b) => {
      if (a.sortScore !== b.sortScore) {
        return a.sortScore - b.sortScore;
      }

      return getColorSortKey(a.color).localeCompare(
        getColorSortKey(b.color),
        "en",
        { sensitivity: "base" }
      );
    });
};

export const getColorBrightness = (hex: string): number => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  // Brightness formula: 0.299*R + 0.587*G + 0.114*B
  return 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
};

export const isPaleColor = (hex: string): boolean => {
  return getColorBrightness(hex) > 240;
};

interface ParsedPantoneColor {
  original: string;
  base: string;
  number: number | null;
  suffix: string;
}

const parsePantoneColor = (color: string): ParsedPantoneColor => {
  const withoutPrefix = color.replace(/^PANTONE\s+/i, "");

  const match = withoutPrefix.match(/^(.+?)\s*(\d+)?\s*([A-Z]\s*)?$/i);

  if (match) {
    const base = match[1].trim();
    const number = match[2] ? parseInt(match[2], 10) : null;
    const suffix = match[3] ? match[3].trim() : "";

    return { original: color, base, number, suffix };
  }

  return { original: color, base: withoutPrefix, number: null, suffix: "" };
};

export const sortPantoneColors = (colors: PantoneColor[]): PantoneColor[] => {
  const parsed = colors.map((color) => ({
    color,
    parsed: parsePantoneColor(color.name),
  }));

  parsed.sort((a, b) => {
    const baseCompare = a.parsed.base.localeCompare(b.parsed.base, "en", {
      sensitivity: "base",
    });
    if (baseCompare !== 0) return baseCompare;

    if (a.parsed.number === null && b.parsed.number === null) return 0;
    if (a.parsed.number === null) return -1;
    if (b.parsed.number === null) return 1;

    const numberCompare = a.parsed.number - b.parsed.number;
    if (numberCompare !== 0) return numberCompare;

    return a.parsed.suffix.localeCompare(b.parsed.suffix, "en", {
      sensitivity: "base",
    });
  });

  return parsed.map((p) => p.color);
};

export const formatPantoneName = (name: string): string => {
  return name.replace(/PANTONE/g, "Pantone");
};
